import React from 'react';
import { Box, Button } from '@mui/material';
import { AppDataGrid } from '@/components/ui';
import { AppCard } from '@/components/app-card/app-card';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import { hasUserAccessTo } from '@/lib/permissions';
import { PermissionArea } from '@cloudcost/shared-types/users';
import { SystemData, PendingSystemBudgetChange } from '../../calculations';
import { getSystemsColumns } from './systems-columns';

interface SystemsTableProps {
  items: SystemData[];
  pendingBudgetChanges: Map<number, PendingSystemBudgetChange>;
  departmentBudgets: Map<number, { value: number; currency: string; name: string }>;
  onEditSystem: (system: SystemData) => void;
  onSetBudget: (system: SystemData) => void;
  onCreateSystem: () => void;
  onBulkSave: () => void;
  onCancelChanges: () => void;
  bulkSaveLoading: boolean;
  canSetSystemBudget: (system: SystemData) => boolean;
}

export const SystemsTable: React.FC<SystemsTableProps> = ({
  items,
  pendingBudgetChanges,
  departmentBudgets,
  onEditSystem,
  onSetBudget,
  onCreateSystem,
  onBulkSave,
  onCancelChanges,
  bulkSaveLoading,
  canSetSystemBudget,
}) => {
  const canCreateSystem = hasUserAccessTo(PermissionArea.SYSTEM_CREATE);

  const columns = getSystemsColumns({
    pendingBudgetChanges,
    departmentBudgets,
    onEditSystem,
    onSetBudget,
    canSetSystemBudget,
  });

  return (
    <AppCard
      className="card100"
      title="Systems List"
      icon={<BarChartOutlinedIcon color="primary" />}
      headerRight={
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          {canCreateSystem && (
            <Button
              variant="contained"
              onClick={onCreateSystem}
              color="primary"
              data-testid="systems-add">
              Add
            </Button>
          )}
        </Box>
      }
      footerRight={
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          {pendingBudgetChanges.size > 0 && (
            <Button
              variant="outlined"
              onClick={onCancelChanges}
              color="inherit"
              style={{
                marginTop: '10px',
                marginBottom: '10px',
              }}>
              Cancel Changes ({pendingBudgetChanges.size})
            </Button>
          )}
          <Button
            variant="contained"
            onClick={onBulkSave}
            color="primary"
            disabled={pendingBudgetChanges.size === 0 || bulkSaveLoading}
            style={{
              float: 'right',
              marginTop: '10px',
              marginBottom: '10px',
            }}
            data-testid="systems-set-budgets">
            {bulkSaveLoading
              ? 'Saving...'
              : `Set Budgets${
                  pendingBudgetChanges.size > 0
                    ? ` (${pendingBudgetChanges.size})`
                    : ''
                }`}
          </Button>
        </Box>
      }>
      <AppDataGrid
        style={{ width: '100%', height: '400px', marginLeft: '0px' }}
        rows={items}
        columns={columns}
      />
    </AppCard>
  );
};